import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Award, ArrowLeft, ArrowRight, Trophy, RotateCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function TrainingExam() {
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);          // {exam_id, questions, pass_mark, total}
  const [answers, setAnswers] = useState({});       // {qid: idx}
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);       // {attempt, detail, certificate}

  const start = async () => {
    setResult(null); setAnswers({}); setIdx(0); setExam(null);
    try {
      const r = await api.get("/training/exam");
      setExam(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot start exam");
      navigate("/app/training");
    }
  };
  useEffect(() => { start(); /* eslint-disable-next-line */ }, []);

  const total = exam?.questions?.length || 0;
  const answeredCount = Object.keys(answers).length;
  const q = exam?.questions?.[idx];

  const select = (qid, optIdx) => setAnswers((a) => ({ ...a, [qid]: optIdx }));

  const submit = async () => {
    if (answeredCount < total) {
      const ok = window.confirm(`You've answered ${answeredCount} of ${total}. Submit anyway?`);
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/training/exam/submit", { answers });
      setResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    finally { setSubmitting(false); }
  };

  // Map for showing correct/picked in review
  const detailByQid = useMemo(() => {
    const m = {};
    (result?.detail || []).forEach((d) => { m[d.question_id] = d; });
    return m;
  }, [result]);

  if (!exam) return <div className="text-stone-500" data-testid="exam-loading">Loading exam...</div>;

  // ----- RESULT VIEW -----
  if (result) {
    const a = result.attempt;
    return (
      <div className="space-y-6" data-testid="exam-result">
        <div className={`rounded-2xl p-7 text-center ${a.passed ? "bg-gradient-to-br from-emerald-900 to-emerald-950 text-amber-50" : "bg-stone-100 border border-stone-300"}`}>
          {a.passed ? <Trophy className="w-12 h-12 mx-auto text-amber-300" /> : <XCircle className="w-12 h-12 mx-auto text-stone-500" />}
          <div className="font-display text-3xl font-black mt-3" data-testid="exam-result-headline">
            {a.passed ? "Congratulations — you passed!" : "Not quite there yet"}
          </div>
          <div className="font-mono text-lg mt-2" data-testid="exam-result-score">
            Score: {a.score} / {a.total} · {a.percentage}% (pass mark {a.pass_mark}%)
          </div>
          {a.passed && result.certificate && (
            <Link to="/app/certificates" data-testid="exam-result-cert-link"
              className="inline-flex items-center gap-2 mt-5 rounded-full bg-amber-400 text-emerald-900 px-6 py-3 font-bold hover:bg-amber-300">
              <Award className="w-4 h-4" /> Download Your Certificate <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          {!a.passed && (
            <Button onClick={start} className="mt-5 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold" data-testid="exam-retake-btn">
              <RotateCw className="w-4 h-4 mr-2" /> Try Again
            </Button>
          )}
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-200 bg-stone-50 text-xs font-bold uppercase tracking-wider text-stone-600">Question-by-question review</div>
          <div className="divide-y divide-stone-100">
            {exam.questions.map((qq, i) => {
              const d = detailByQid[qq.id];
              return (
                <div key={qq.id} className="p-5" data-testid={`exam-review-${qq.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-xs font-bold ${d?.correct ? "bg-emerald-100 text-emerald-900" : "bg-red-100 text-red-700"}`}>
                      {d?.correct ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-stone-900">Q{i + 1}. {qq.text}</div>
                      <div className="mt-3 space-y-1.5">
                        {qq.options.map((opt, oi) => {
                          const isCorrect = oi === d?.correct_index;
                          const isPicked = oi === d?.picked;
                          return (
                            <div key={oi} className={`text-sm rounded-lg px-3 py-2 border ${
                              isCorrect ? "bg-emerald-50 border-emerald-300 text-emerald-900 font-semibold" :
                              isPicked  ? "bg-red-50 border-red-300 text-red-700" :
                              "border-stone-200 text-stone-600"
                            }`}>
                              {String.fromCharCode(65 + oi)}. {opt}
                              {isCorrect && <span className="ml-2 text-xs font-bold uppercase">Correct</span>}
                              {isPicked && !isCorrect && <span className="ml-2 text-xs font-bold uppercase">Your answer</span>}
                            </div>
                          );
                        })}
                      </div>
                      {d?.explanation && (
                        <div className="mt-3 text-xs text-stone-600 bg-stone-50 rounded-lg p-3 italic" data-testid={`exam-review-explanation-${qq.id}`}>
                          <b className="not-italic text-stone-700">Why:</b> {d.explanation}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Link to="/app/training" className="inline-flex items-center text-sm font-bold text-stone-600 hover:text-emerald-900" data-testid="exam-back-to-course">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to course
        </Link>
      </div>
    );
  }

  // ----- EXAM VIEW -----
  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="exam-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Final Exam</span>
          <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tight mt-1" data-testid="exam-headline">
            Question {idx + 1} of {total}
          </h1>
        </div>
        <div className="text-sm text-stone-500" data-testid="exam-answered-count">
          Answered: <b>{answeredCount}</b> / {total}
        </div>
      </div>

      <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-700 transition-all" style={{ width: `${Math.round(((idx + 1) / total) * 100)}%` }} />
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-7" data-testid={`exam-q-${q.id}`}>
        <div className="font-display text-xl font-black text-stone-900 leading-snug" data-testid="exam-q-text">{q.text}</div>
        <div className="mt-5 space-y-2.5">
          {q.options.map((opt, oi) => {
            const checked = answers[q.id] === oi;
            return (
              <label key={oi} data-testid={`exam-q-${q.id}-opt-${oi}`}
                className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                  checked ? "border-emerald-900 bg-emerald-50" : "border-stone-200 hover:border-stone-300 bg-white"
                }`}>
                <input type="radio" name={`q-${q.id}`} checked={checked} onChange={() => select(q.id, oi)}
                  className="mt-1 w-4 h-4 accent-emerald-700" />
                <div className="flex-1 text-sm text-stone-800">
                  <span className="font-bold mr-2">{String.fromCharCode(65 + oi)}.</span>{opt}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button variant="outline" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="exam-prev-btn" className="rounded-full">
          <ArrowLeft className="w-4 h-4 mr-1" /> Previous
        </Button>
        {idx < total - 1 ? (
          <Button onClick={() => setIdx(idx + 1)} data-testid="exam-next-btn" className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
            Next <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={submit} disabled={submitting} data-testid="exam-submit-btn" className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
            {submitting ? "Submitting..." : "Submit Exam"} <CheckCircle2 className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Question palette */}
      <div className="flex flex-wrap gap-1.5 pt-2" data-testid="exam-palette">
        {exam.questions.map((qq, i) => {
          const isCurrent = i === idx;
          const isAnswered = answers[qq.id] !== undefined;
          return (
            <button key={qq.id} onClick={() => setIdx(i)} data-testid={`exam-palette-${i}`}
              className={`w-8 h-8 rounded text-xs font-bold ${
                isCurrent ? "bg-emerald-900 text-white" : isAnswered ? "bg-amber-100 text-amber-900 border border-amber-300" : "bg-stone-100 text-stone-500 border border-stone-200"
              }`}>{i + 1}</button>
          );
        })}
      </div>
    </div>
  );
}
